import * as THREE from 'three';
import { TICK_HZ } from '@somnhold/content';
import { archesNext, archesWarn, leapsJoined, leapsVert, type BattleState } from '@somnhold/sim';
import { simToWorld } from '@somnhold/render';
import { pinMark, remeasureMark } from './mark-pin.ts';
import { t } from '../i18n.ts';
import { h, setStyle } from '../ui/dom.ts';

/** The wind: three lines, the middle one longer. */
const WIND = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M1.5 5h8.5a2 2 0 1 0-2-2M1.5 8.5h11.5a2 2 0 1 1-2 2M1.5 12h6" stroke="currentColor" stroke-width="1.4" fill="none" stroke-linecap="round"/></svg>';
/** A mooring post with its ring. */
const POST = '<svg viewBox="0 0 16 16" aria-hidden="true"><rect x="6.4" y="2" width="3.2" height="12.5" rx="0.6" fill="currentColor"/><circle cx="11.2" cy="7" r="2" stroke="currentColor" stroke-width="1.2" fill="none"/></svg>';
/** A stone boar, side on. */
const BOAR = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2.2 9.2c0-2.6 2.2-4.4 5.3-4.4 2.3 0 3.9.8 4.8 2.1l1.6-.5-.6 1.9c.3.5.5 1 .5 1.6 0 1.5-1.2 2.3-3 2.3H5.1c-1.8 0-2.9-1.2-2.9-3z" fill="currentColor"/><path d="M4.4 12.2v1.6M6.6 12.2v1.6M9.6 12.2v1.6M11.6 12.2v1.6" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>';
/** Sigrun's mark: five strokes and a knot. */
const MARK = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2.5 3v7M5 3v7M7.5 3v7M10 3v7M1.5 9.5l10-5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/><circle cx="13" cy="12.5" r="2" fill="currentColor"/></svg>';
/** An arch over the water. */
const ARCH = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M1.5 6h13v2.2h-1.6c-.6-2-2.6-3.2-4.9-3.2s-4.3 1.2-4.9 3.2H1.5z" fill="currentColor"/><path d="M3.3 8.2v4.3M12.7 8.2v4.3" stroke="currentColor" stroke-width="2"/><path d="M1 14c1.2-.8 2.4-.8 3.6 0s2.4.8 3.6 0 2.4-.8 3.6 0 2.4.8 3.6 0" stroke="currentColor" stroke-width="1" fill="none"/></svg>';
/** The boat. */
const BOAT = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M1.5 8.5h13l-2.3 3.6H3.8z" fill="currentColor"/><path d="M12.8 8.5l1.6-3M4 8.5V6.2M7 8.5V6.2M10 8.5V6.2" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/><path d="M1 14c1.2-.8 2.4-.8 3.6 0s2.4.8 3.6 0 2.4-.8 3.6 0 2.4.8 3.6 0" stroke="currentColor" stroke-width="1" fill="none"/></svg>';
/** A wolf's head raised, howling. */
const HOWL = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3 13.5l1.8-5.2L3.6 4l2.6 1.8L9.4 2l.8 3.4 3.3 1.4-2.7 1.4L9 13.5z" fill="currentColor"/><path d="M12.8 2.6c.9.7 1.4 1.6 1.4 2.6M11.9 1.2c1.5.9 2.4 2.4 2.5 4" stroke="currentColor" stroke-width="1" fill="none" stroke-linecap="round"/></svg>';
/** A loose stone. */
const BOULDER = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3 11.5l1-5 4-3 4 2 1.5 5-3 2.5H5z" fill="currentColor"/><path d="M1 14h14" stroke="currentColor" stroke-width="1.2"/></svg>';

type Mark = { el: HTMLButtonElement; state: HTMLElement; pips: HTMLElement[]; point: THREE.Vector3; at: number };

/**
 * The Vertebrae on screen (docs/sendas/barbaro.md, chapter 2), inside a path's own plate (game/senda-hud.ts) and over
 * the board; a phone's plate kept under about a quarter of the screen:
 *
 * - the arches (`arches`): the next gust (its seconds; warned, «¡Al poste!» red, the side it blows to), Terrón (behind,
 *   balking at the gap: «empujalo», over). Over the board, while the foam lifts, a ring on the nearest posts; Terrón's
 *   mark while he balks (a tap takes the hero to him).
 * - the leaps (`leaps`): Sigrun's marks (a pip each, red when touched) and the rule in a line («cada arco aguanta dos
 *   llegadas»). Over the board, a mark on every arch standing: its arrivals as two pips (the third brings it down), lit
 *   where a rope reaches from the hero's, Sigrun's mark on it; a tap swings the hero over.
 * - the den (`den`): the boat's bar (Bera's work; in the water: «¡al bote!»), each howler (its next howl, rearing:
 *   «¡cortalo!», down). Over the board, the loose stones (a tap sends the hero to push) and the boat once afloat.
 *
 * Plain DOM, written only when it changes (docs/RENDIMIENTO.md); the board's points from the sim's own rules.
 */
export class VertHud {
  readonly panel: HTMLElement;
  private kind: 'arches' | 'leaps' | 'den';
  private v = new THREE.Vector3();
  private rows: HTMLElement[] = [];
  // The arches.
  private gustRow = h('div.bh-row.al-waverow.vt-gust');
  private gustText = h('span.al-wave');
  private terronRow = h('div.bh-row.al-waverow.vt-terron');
  private terronText = h('span.al-wave');
  private posts: HTMLElement[] = [];
  // The leaps.
  private markRow = h('div.bh-row.vt-marks');
  private markPips: HTMLElement[] = [];
  private markText = h('span.al-wave');
  private ruleRow = h('div.bh-row.vt-rule');
  // The den.
  private boatRow = h('div.bh-row.vt-boat');
  private boatFill = h('i');
  private boatText = h('small.vt-boat-s');
  private howlRows: { row: HTMLElement; text: HTMLElement }[] = [];
  private marks: Mark[] = [];
  private key = '';

  constructor(
    s: BattleState,
    private board: HTMLElement,
    private mark: (at: number) => void,
  ) {
    const vr = s.rules?.senda?.vert;
    this.kind = vr?.arches ? 'arches' : vr?.leaps ? 'leaps' : 'den';
    if (vr?.arches) {
      this.gustRow.append(h('span.bh-ico', { html: WIND }), this.gustText);
      this.terronRow.append(h('span.bh-ico', { html: BOAR }), h('span.bh-label', {}, t('senda.vert.terron')), this.terronText);
      this.rows.push(this.gustRow, this.terronRow);
      for (let i = 0; i < 2; i++) {
        const c = h('div.vt-post', {}, h('span.vt-post-i', { html: POST }), t('senda.vert.post'));
        c.hidden = true;
        this.board.append(c);
        this.posts.push(c);
      }
      this.marks.push(this.make(0, BOAR, t('senda.vert.terron'), t('senda.vert.terronHint'), 'terron', 0));
    } else if (vr?.leaps) {
      const r = vr.leaps;
      this.markPips = r.marks.map(() => h('i'));
      this.markRow.append(h('span.bh-ico', { html: MARK }), h('span.bh-label', {}, t('senda.vert.marks')), h('span.al-pips.vt-pips', {}, ...this.markPips), this.markText);
      this.ruleRow.append(h('span.bh-ico', { html: ARCH }), h('span.vt-rule-t', {}, t('senda.vert.rule')));
      this.rows.push(this.markRow, this.ruleRow);
      const v = leapsVert(r);
      r.arches.forEach((p, k) => {
        const m = this.make(k, k === v ? BOAT : ARCH, k === v ? t('senda.vert.vertebra') : '', t('senda.vert.archHint'), `arch-${k}`, k === v ? 0 : 2);
        simToWorld(p.x, p.y, m.point, 0.4);
        this.marks.push(m);
      });
    } else if (vr?.den) {
      const r = vr.den;
      this.boatRow.append(h('span.bh-ico', { html: BOAT }), h('span.bh-label', {}, t('senda.vert.boat')), h('span.bar.vt-bar', {}, this.boatFill), this.boatText);
      this.rows.push(this.boatRow);
      for (let i = 0; i < 2; i++) {
        const text = h('span.al-wave');
        const row = h('div.bh-row.al-waverow.vt-howl', {}, h('span.bh-ico', { html: HOWL }), h('span.bh-label', {}, t('senda.vert.howler', { n: i + 1 })), text);
        this.howlRows.push({ row, text });
        this.rows.push(row);
      }
      const boat = this.make(0, BOAT, t('senda.vert.boat'), t('senda.vert.boatHint'), 'boat', 0);
      simToWorld(r.boat.x, r.boat.y, boat.point, 0.4);
      this.marks.push(boat);
      r.stones.forEach((p, k) => {
        const m = this.make(k + 1, BOULDER, t('senda.vert.stone'), t('senda.vert.stoneHint'), `stone-${k}`, 0);
        simToWorld(p.x, p.y, m.point, 0.55);
        this.marks.push(m);
      });
    }
    this.panel = h(`div.bh.cz-panel.vt-panel.vt-${this.kind}`, {}, ...this.rows);
  }

  private make(at: number, icon: string, name: string, hint: string, test: string, pips: number): Mark {
    const state = h('small.al-state.cz-state');
    const ps = Array.from({ length: pips }, () => h('i'));
    const el = h(
      `button.al-stone.cz-mark.vt-mark.vt-${test.split('-')[0]}`,
      { type: 'button', 'data-test': `vert-${test}`, 'aria-label': hint, title: hint, onpointerup: (e: Event) => (e.stopPropagation(), this.mark(at)) },
      h('span.al-ring'),
      h('span.al-glyph', { html: icon }),
      name ? h('b.mn-name.cz-name', {}, name) : '',
      pips ? h('span.vt-cracks', {}, ...ps) : '',
      state,
    ) as HTMLButtonElement;
    this.board.append(el);
    return { el, state, pips: ps, point: new THREE.Vector3(), at };
  }

  private setText(el: HTMLElement, s: string): void {
    if (el.textContent !== s) el.textContent = s;
  }

  private updateArches(s: BattleState): void {
    const r = s.rules?.senda?.vert?.arches;
    const w = s.senda?.vert?.arches;
    if (!r || !w) return;
    const warn = archesWarn(s, r, w);
    const left = archesNext(s, r, w);
    const g = r.plan[w.gust];
    const side = g ? t(g.dir > 0 ? 'senda.vert.toEast' : 'senda.vert.toWest') : '';
    this.setText(this.gustText, w.done >= 0 ? t('senda.vert.arrived') : warn ? t('senda.vert.gustNow', { s: Math.max(1, Math.ceil(left / TICK_HZ)), side }) : left >= 0 ? t('senda.vert.gustIn', { s: Math.ceil(left / TICK_HZ) }) : '');
    this.gustRow.classList.toggle('warn', warn && w.done < 0);
    const tt = w.balk === 1 ? t('senda.vert.terronBalk') : w.balk === 2 ? t('senda.vert.terronOver') : t('senda.vert.terronBehind');
    this.setText(this.terronText, tt);
    this.terronRow.classList.toggle('warn', w.balk === 1);
    const m = this.marks[0];
    const tn = s.senda?.npcs.find((x) => x.id === 'terron');
    if (m && tn) {
      simToWorld(tn.x, tn.y, m.point, 0.5);
      const gone = w.balk !== 1 || w.done >= 0 || s.result !== 'running';
      m.el.classList.toggle('gone', gone);
      m.el.disabled = gone;
      m.el.classList.toggle('sent', w.held > 0);
      m.el.style.setProperty('--k', Math.min(1, w.held / Math.max(1, r.nudge)).toFixed(2));
    }
  }

  private updateLeaps(s: BattleState): void {
    const r = s.rules?.senda?.vert?.leaps;
    const w = s.senda?.vert?.leaps;
    if (!r || !w) return;
    const n = w.touched.filter((x) => x).length;
    w.touched.forEach((x, i) => this.markPips[i]?.classList.toggle('on', x));
    this.setText(this.markText, w.stuck >= 0 ? t('senda.vert.stuck') : w.done >= 0 ? t('senda.vert.leapsDone') : t('senda.vert.marksN', { n, of: w.touched.length }));
    this.markRow.classList.toggle('warn', w.stuck >= 0);
    const key = `${w.on}|${w.to}|${w.cracks.join('')}|${w.fallen.join('')}|${w.marks.join(',')}|${w.touched.join('')}|${w.done}|${w.stuck}`;
    if (key === this.key) return;
    this.key = key;
    const v = leapsVert(r);
    for (const m of this.marks) {
      const k = m.at;
      const fallen = !!w.fallen[k];
      const gone = fallen || w.done >= 0 || w.stuck >= 0 || s.result !== 'running';
      const here = w.on === k;
      const reach = !here && !fallen && w.to < 0 && leapsJoined(r, w.on, k);
      const c = w.cracks[k] ?? 0;
      m.pips.forEach((p, i) => p.classList.toggle('on', c > i));
      m.el.classList.toggle('gone', gone);
      m.el.disabled = gone || !reach;
      m.el.classList.toggle('reach', reach);
      m.el.classList.toggle('here', here);
      m.el.classList.toggle('worn', c >= 2 && k !== v);
      const marked = w.marks.some((x, i) => x === k && !w.touched[i]);
      m.el.classList.toggle('sigrun', marked);
      const st = here ? t('senda.vert.here') : marked ? t('senda.vert.markHere') : c >= 2 && k !== v ? t('senda.vert.worn') : '';
      if (m.state.textContent !== st) {
        m.state.textContent = st;
        remeasureMark(m.el);
      }
    }
  }

  private updateDen(s: BattleState): void {
    const r = s.rules?.senda?.vert?.den;
    const w = s.senda?.vert?.den;
    if (!r || !w) return;
    const k = Math.min(1, w.work / Math.max(1, r.launch));
    setStyle(this.boatFill, 'width', `${(k * 100).toFixed(1)}%`);
    const bera = s.senda?.npcs.find((x) => x.id === 'bera');
    const hurt = !!bera && bera.flinch > 0 && w.launched < 0;
    this.setText(this.boatText, w.launched >= 0 ? t('senda.vert.boatIn') : hurt ? t('senda.vert.beraHit') : t('senda.vert.boatLeft', { s: Math.max(0, Math.ceil((r.launch - w.work) / TICK_HZ)) }));
    this.boatRow.classList.toggle('warn', hurt);
    this.boatRow.classList.toggle('go', w.launched >= 0);
    w.howlers.forEach((hw, i) => {
      const row = this.howlRows[i];
      if (!row) return;
      const txt = hw.phase === 3 ? t('senda.caza.howlerDown') : hw.phase === 1 ? t('senda.caza.rear', { s: Math.max(1, Math.ceil((hw.until - s.tick + r.howler.howl) / TICK_HZ)) }) : hw.phase === 2 ? t('senda.caza.howl') : t('senda.caza.howlIn', { s: Math.max(0, Math.ceil((hw.next - s.waveT) / TICK_HZ)) });
      this.setText(row.text, txt);
      row.row.classList.toggle('warn', hw.phase === 1 || hw.phase === 2);
      row.row.classList.toggle('down', hw.phase === 3);
    });
    const hot = w.howlers.some((x) => x.phase === 1 || x.phase === 2);
    for (const m of this.marks) {
      let gone = w.done >= 0 || s.result !== 'running';
      if (m.at === 0) gone = gone || w.launched < 0;
      else {
        const st = w.stones[m.at - 1];
        gone = gone || !st || st.mode !== 0;
        m.el.classList.toggle('sent', w.job === m.at);
        m.el.style.setProperty('--k', w.job === m.at ? Math.min(1, w.held / Math.max(1, r.stone.stand)).toFixed(2) : '0');
        m.el.classList.toggle('hot', hot);
      }
      m.el.classList.toggle('gone', gone);
      m.el.disabled = gone;
    }
  }

  update(s: BattleState): void {
    if (this.kind === 'arches') this.updateArches(s);
    else if (this.kind === 'leaps') this.updateLeaps(s);
    else this.updateDen(s);
  }

  /** Every frame: the marks over their places; while the foam lifts, the nearest posts. */
  place(s: BattleState | undefined, camera: THREE.Camera, w: number, ht: number): void {
    for (const m of this.marks) {
      if (m.el.classList.contains('gone')) {
        setStyle(m.el, 'display', 'none');
        continue;
      }
      this.v.copy(m.point).project(camera);
      if (this.v.z > 1 || this.v.x < -1.1 || this.v.x > 1.1 || this.v.y < -1.1 || this.v.y > 1.1) {
        setStyle(m.el, 'display', 'none');
        continue;
      }
      setStyle(m.el, 'display', '');
      pinMark(m.el, Math.min(w - 40, Math.max(40, (this.v.x * 0.5 + 0.5) * w)), (-this.v.y * 0.5 + 0.5) * ht);
    }
    const r = s?.rules?.senda?.vert?.arches;
    const ws = s?.senda?.vert?.arches;
    const hero = s?.heroes[0];
    const warn = !!r && !!ws && !!s && ws.done < 0 && archesWarn(s, r, ws);
    if (!warn || !r || !hero) {
      for (const c of this.posts) c.hidden = true;
      return;
    }
    const near = r.posts
      .map((p, i) => ({ i, d: (p.x - hero.x) ** 2 + (p.y - hero.y) ** 2 }))
      .sort((a, b) => a.d - b.d)
      .slice(0, this.posts.length);
    near.forEach((n, k) => {
      const c = this.posts[k] as HTMLElement;
      const p = r.posts[n.i] as { x: number; y: number };
      simToWorld(p.x, p.y, this.v, 1.2).project(camera);
      c.hidden = this.v.z > 1;
      pinMark(c, Math.min(w - 30, Math.max(30, (this.v.x * 0.5 + 0.5) * w)), (-this.v.y * 0.5 + 0.5) * ht - 18);
    });
  }
}
